import { createSlice } from "@reduxjs/toolkit";
import { decorateComms } from "../utils/decorateComms";

const initialState = {
  byId: {},
  threadMap: {},
  pendingIds: [],
  pageMap: {},
  currentPageUrl: null,
  currentPageNumber: 1,
  newMessages: {
    byId: {},
    allIds: []
  },
  hasNewMessage: false // <-- Add this flag
};

// ─── SYNC UTILITY ─────────────────────────────────────────────────────────────
function syncNewMessagesWithById(state, msgId) {
  const updated = state.byId[msgId];
  if (state.newMessages.byId[msgId]) {
    state.newMessages.byId[msgId] = {
      ...state.newMessages.byId[msgId],
      ...updated,
      meta: {
        ...(state.newMessages.byId[msgId].meta || {}),
        ...(updated.meta || {})
      }
    };
  }
}

const messageSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    addMessage: (state, action) => {
      //console.log("Adding/Updating message:", action.payload);
      const msg = action.payload;
      const msgId = msg.id;
      const existing = state.byId[msgId] || {};

      state.byId[msgId] = {
        id: msgId,
        ...msg,
        extension: msg.extension ?? existing.extension,
        meta: {
          ...(existing.meta || {}),
          ...(msg.meta || {})
        },
        updatedAt: new Date().toISOString()
      };

      const anchorRef =
        msg.partOf?.[0]?.reference ||
        msg.inResponseTo?.[0]?.reference ||
        msg.id;
      const anchorId = anchorRef?.replace("Communication/", "") || msg.id;

      if (!state.threadMap[anchorId]) {
        state.threadMap[anchorId] = [];
      }
      if (!state.threadMap[anchorId].includes(msgId)) {
        state.threadMap[anchorId].push(msgId);
      }

      const isPending = msg.meta?.isPending;
      const isAlreadyTracked = state.pendingIds.includes(msgId);

      if (isPending && !isAlreadyTracked) {
        state.pendingIds.push(msgId);
      } else if (!isPending && isAlreadyTracked) {
        state.pendingIds = state.pendingIds.filter(id => id !== msgId);
      }

      syncNewMessagesWithById(state, msgId);
    },

    setFullThread: (state, action) => {
      const { anchorId, messages } = action.payload;

      messages.forEach(msg => {
        state.byId[msg.id] = {
          ...(state.byId[msg.id] || {}),
          ...msg
        };
      });

      state.threadMap[anchorId] = messages.map(m => m.id);
      const confirmedIds = new Set(messages.map(m => m.id));
      state.pendingIds = state.pendingIds.filter(id => !confirmedIds.has(id));
    },

    setPageMessages: (state, action) => {
      const { pageUrl, messages, nextPageUrl, prevPageUrl, total } = action.payload;

      messages.forEach(msg => {
        const decorated = decorateComms(msg);
        const existing = state.byId[decorated.id] || {};

        state.byId[decorated.id] = {
          ...existing,
          ...decorated,
          meta: {
            ...existing.meta,
            ...decorated.meta
          }
        };
      });

      state.pageMap[pageUrl] = {
        ids: messages.map(msg => msg.id),
        nextPageUrl,
        prevPageUrl,
        total
      };

      const confirmedIds = new Set(messages.map(m => m.id));
      state.pendingIds = state.pendingIds.filter(id => !confirmedIds.has(id));
    },

    setCurrentPageUrl: (state, action) => {
      state.currentPageUrl = action.payload;
    },

    setPageNumber: (state, action) => {
      state.currentPageNumber = action.payload;
    },

    incrementPageNumber: (state) => {
      state.currentPageNumber += 1;
    },

    decrementPageNumber: (state) => {
      if (state.currentPageNumber > 1) {
        state.currentPageNumber -= 1;
      }
    },

    clearMessages: (state) => {
      state.byId = {};
      state.threadMap = {};
      state.pendingIds = [];
      state.pageMap = {};
      state.currentPageUrl = null;
      state.currentPageNumber = 1;
      state.newMessages = { byId: {}, allIds: [] };
    },

    updateHydration: (state, action) => {
      const { id, enriched } = action.payload;
      const msg = state.byId[id];

      if (!msg) return;

      state.byId[id] = {
        ...msg,
        meta: {
          ...(msg.meta || {}),
          hydrated: true,
          enrichedData: enriched,
          hydratedAt: new Date().toISOString()
        }
      };
    },

    addNewMessages: (state, action) => {
      console.log("Adding new messages:", action.payload);
      const incoming = action.payload;
      let added = false;

      incoming.forEach((msg) => {
        const msgId = msg.id;
        const alreadyInNew = !!state.newMessages.byId[msgId];
        const alreadyInMain = !!state.byId[msgId];

        if (!alreadyInNew) {
          state.newMessages.byId[msgId] = {
            ...msg,
            meta: {
              ...(msg.meta || {}),
              isPending: false,
              isNew: true
            }
          };
          state.newMessages.allIds.unshift(msgId);
          added = true; // <-- Set flag if a new message is added
        }

        if (!alreadyInMain) {
          state.byId[msgId] = {
            ...msg,
            meta: {
              ...(msg.meta || {}),
              isPending: false,
              isNew: true
            }
          };

          const anchorRef =
            msg.partOf?.[0]?.reference ||
            msg.inResponseTo?.[0]?.reference ||
            msg.id;
          const anchorId = anchorRef?.replace("Communication/", "") || msg.id;

          if (!state.threadMap[anchorId]) {
            state.threadMap[anchorId] = [];
          }
          if (!state.threadMap[anchorId].includes(msgId)) {
            state.threadMap[anchorId].push(msgId);
          }
        }
      });

      if (added) {
        state.hasNewMessage = true; // <-- Set flag if any new message was added
      }
    },

    clearNewMessages: (state) => {
      state.newMessages.byId = {};
      state.newMessages.allIds = [];
      state.hasNewMessage = false; // <-- Reset flag when clearing new messages
    },

    acknowledgeNewMessage: (state, action) => {
      const msgId = action.payload;
      if (state.newMessages.byId[msgId]) {
        delete state.newMessages.byId[msgId];
        state.newMessages.allIds = state.newMessages.allIds.filter(id => id !== msgId);
      }
    },

    setPageNumber: (state, action) => {
      state.currentPageNumber = action.payload;
    },

    incrementPageNumber: (state) => {
      state.currentPageNumber += 1;
    },

    decrementPageNumber: (state) => {
      if (state.currentPageNumber > 1) {
        state.currentPageNumber -= 1;
      }
    },

  }
});

export const {
  addMessage,
  setFullThread,
  setPageMessages,
  setCurrentPageUrl,
  setPageNumber,
  incrementPageNumber,
  decrementPageNumber,
  clearMessages,
  updateHydration,
  addNewMessages,
  clearNewMessages,
  acknowledgeNewMessage,

} = messageSlice.actions;

export default messageSlice.reducer;